import { gql, useMutation, useQuery } from "@apollo/client";
import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useMount, useTitle } from "react-use";
import profileImage from "../../../assets/images/profile.svg";
import Button from "../../../components/Button";
import { FormField } from "../../../components/forms/shared";
import TextInput from "../../../components/forms/TextInput";
import Select from "../../../components/Select";
import { STATES } from "../../../constants/states";
import {
  ROUTE_ONBOARDING_FINANCIAL,
  ROUTE_ONBOARDING_PLANS,
} from "../../../routes";
import { StepBack, StepContainer, StepHeader } from "../components/shared";

const Contact = () => {
  useTitle("Onboarding - Contact Information | Due");
  const history = useHistory();
  const [stateValue, setStateValue] = useState<{
    label: string;
    value: string;
  } | null>();
  const { data, refetch, loading } = useQuery(gql`
    {
      self {
        firstName
        middleName
        lastName
        phoneNumber
      }
      profile {
        addressLine1
        addressLine2
        city
        state
        zipCode
        country
      }
    }
  `);

  const [updateSelf] = useMutation(gql`
    mutation UpdateSelf($input: SelfMutationInput!) {
      self(input: $input) {
        firstName
      }
    }
  `);

  const [updateProfile] = useMutation(gql`
    mutation UpdateProfile($input: ProfileMutationInput!) {
      profile(input: $input) {
        addressLine1
      }
    }
  `);

  const { firstName, middleName, lastName, phoneNumber } =
    data && data.self
      ? data.self
      : {
          firstName: "",
          middleName: "",
          lastName: "",
          phoneNumber: "",
        };

  const { addressLine1, addressLine2, city, state, zipCode } =
    data && data.profile
      ? data.profile
      : {
          addressLine1: "",
          addressLine2: "",
          city: "",
          state: "",
          zipCode: "",
        };

  useMount(() => {
    document.getElementById("onboarding-container")?.scrollTo({ top: 0 });
    refetch();
  });

  useEffect(() => {
    if (!loading && data && state) {
      setStateValue(STATES.find((s) => s.value === state) || null);
    }
  }, [data, loading, state]);

  return (
    <StepContainer className="lg:w-8/12 md:w-10/12 min-h-0">
      <div className="w-6/12 pr-8">
        <div className="flex items-center">
          <StepBack
            className="mr-2"
            onClick={() => history.push(ROUTE_ONBOARDING_PLANS)}
          />
          <StepHeader>Contact Information</StepHeader>
        </div>
        <div className="prose mt-6">
          <p>
            To sign up for Due you must have a valid U.S mailing address. We
            will never sell this information to anyone.
          </p>
        </div>
        <img className="w-full mt-10" src={profileImage} alt="Profile" />
      </div>
      <div className="w-6/12">
        <div className="border border-gray-400 rounded-md bg-white p-8 mb-16">
          <Formik
            validateOnChange={false}
            validateOnBlur={true}
            enableReinitialize
            initialValues={{
              firstName,
              middleName,
              lastName,
              addressLine1,
              addressLine2,
              city,
              state,
              zipCode,
              country: "United States",
              phoneNumber,
            }}
            onSubmit={({
              firstName,
              lastName,
              middleName,
              addressLine1,
              addressLine2,
              city,
              state,
              zipCode,
              country,
              phoneNumber,
            }) => {
              Promise.all([
                updateSelf({
                  variables: {
                    input: { firstName, lastName, middleName, phoneNumber },
                  },
                }),
                updateProfile({
                  variables: {
                    input: {
                      addressLine1,
                      addressLine2,
                      city,
                      state,
                      zipCode,
                      country,
                    },
                  },
                }),
              ])
                .then(() => {
                  refetch();
                  history.push(ROUTE_ONBOARDING_FINANCIAL);
                })
                .catch(() => {});
            }}
          >
            {({ values, errors, setFieldValue }) => (
              <Form>
                <div className="flex">
                  <FormField
                    className="w-8/12"
                    id="firstName"
                    label="First name"
                    error={errors.firstName}
                  >
                    <TextInput
                      id="firstName"
                      name="firstName"
                      value={values.firstName}
                      onChange={(e) => {
                        setFieldValue("firstName", e.target.value);
                      }}
                    />
                  </FormField>
                  <FormField
                    className="ml-2 w-4/12"
                    id="middleName"
                    label="Middle name"
                    error={errors.middleName}
                  >
                    <TextInput
                      id="middleName"
                      name="middleName"
                      value={values.middleName}
                      onChange={(e) => {
                        setFieldValue("middleName", e.target.value);
                      }}
                    />
                  </FormField>
                </div>
                <FormField
                  id="lastName"
                  label="Last name"
                  error={errors.lastName}
                >
                  <TextInput
                    id="lastName"
                    name="lastName"
                    value={values.lastName}
                    onChange={(e) => {
                      setFieldValue("lastName", e.target.value);
                    }}
                  />
                </FormField>
                <FormField
                  id="addressLine1"
                  label="Address Line 1"
                  error={errors.addressLine1}
                >
                  <TextInput
                    id="addressLine1"
                    name="addressLine1"
                    value={values.addressLine1}
                    onChange={(e) => {
                      setFieldValue("addressLine1", e.target.value);
                    }}
                  />
                </FormField>
                <FormField
                  id="addressLine2"
                  label="Address Line 2"
                  error={errors.addressLine2}
                >
                  <TextInput
                    id="addressLine2"
                    name="addressLine2"
                    value={values.addressLine2}
                    onChange={(e) => {
                      setFieldValue("addressLine2", e.target.value);
                    }}
                  />
                </FormField>
                <div className="flex">
                  <FormField
                    className="w-6/12 mr-1"
                    id="city"
                    label="City"
                    error={errors.city}
                  >
                    <TextInput
                      id="city"
                      name="city"
                      value={values.city}
                      onChange={(e) => {
                        setFieldValue("city", e.target.value);
                      }}
                    />
                  </FormField>
                  <FormField
                    className="w-6/12 ml-1"
                    id="state"
                    label="State"
                    error={errors.state}
                  >
                    <Select
                      placeholder="Select state..."
                      value={stateValue}
                      options={STATES}
                      onChange={(value) => {
                        if (value) {
                          setFieldValue("state", value.value);
                          setStateValue(value);
                        }
                      }}
                    />
                  </FormField>
                </div>
                <div className="flex">
                  <FormField
                    className="w-6/12 mr-1"
                    id="zipCode"
                    label="Zip Code"
                    error={errors.zipCode}
                  >
                    <TextInput
                      id="zipCode"
                      name="zipCode"
                      value={values.zipCode}
                      onChange={(e) => {
                        setFieldValue("zipCode", e.target.value);
                      }}
                    />
                  </FormField>
                  <FormField
                    id="country"
                    label="Country"
                    className="w-6/12 ml-1"
                  >
                    <TextInput
                      id="country"
                      name="country"
                      value={values.country}
                      disabled
                    />
                  </FormField>
                </div>

                <FormField
                  id="phoneNumber"
                  label="Phone Number"
                  error={errors.phoneNumber}
                >
                  <TextInput
                    id="phoneNumber"
                    name="phoneNumber"
                    value={values.phoneNumber}
                    onChange={(e) => {
                      setFieldValue("phoneNumber", e.target.value);
                    }}
                  />
                </FormField>
                <Button type="submit" size="lg">
                  Continue
                </Button>
              </Form>
            )}
          </Formik>
        </div>
        <div className="w-full h-10"></div>
      </div>
    </StepContainer>
  );
};

export default Contact;
